import { Icon } from '@iconify/react'
import { formatDateTime } from '@/lib/campaign-dashboard'
import {
  countMonitoringProbes,
  monitoringStatusLabel,
} from '@/lib/monitoring-panel'
import type { AdminMonitoringPanel, AdminMonitoringStatus } from '@/types/api'
import styles from './MonitoramentoPage.module.css'

interface MonitoringHeroProps {
  panel: AdminMonitoringPanel | null
  isLoading: boolean
  isRefreshing?: boolean
  isCheckingNow?: boolean
  error: string | null
}

function statusIcon(status: AdminMonitoringStatus): string {
  if (status === 'ok') return 'mdi:check-decagram'
  if (status === 'degraded') return 'mdi:alert-decagram'
  return 'mdi:close-octagon'
}

export function MonitoringHero({
  panel,
  isLoading,
  isRefreshing = false,
  isCheckingNow = false,
  error,
}: MonitoringHeroProps) {
  const status: AdminMonitoringStatus | 'loading' | 'unknown' = panel
    ? panel.status
    : isLoading
      ? 'loading'
      : 'unknown'

  const isUpdating = Boolean(panel && (isRefreshing || isCheckingNow))
  const headline =
    status === 'loading'
      ? 'Verificando o sistema...'
      : status === 'unknown'
        ? 'Não foi possível verificar'
        : isUpdating
          ? `${monitoringStatusLabel(status)} · Atualizando...`
          : monitoringStatusLabel(status)

  const counts = panel ? countMonitoringProbes(panel.probes) : null
  const failing = panel?.probes.filter((probe) => probe.status !== 'ok') ?? []

  return (
    <section
      className={styles.hero}
      data-status={status}
      aria-live={status === 'critical' || status === 'unknown' ? 'assertive' : 'polite'}
    >
      <div className={styles.heroMain}>
        <p className={styles.heroKicker}>Situação agora</p>
        <h1 className={styles.heroStatus}>
          {status !== 'loading' && status !== 'unknown' ? (
            <Icon icon={statusIcon(status)} className={styles.heroIcon} aria-hidden />
          ) : null}
          {headline}
        </h1>
        {counts ? (
          <p className={styles.heroMeta}>
            {counts.ok} ok · {counts.degraded} alerta · {counts.critical} falha
            {panel ? ` · checado ${formatDateTime(panel.generated_at)} · ${panel.duration_ms} ms` : ''}
          </p>
        ) : (
          <p className={styles.heroMeta}>
            {isLoading
              ? 'Consultando as sondas do sistema.'
              : error ?? 'Abra esta página como administração, sem instituição selecionada.'}
          </p>
        )}
      </div>

      {failing.length > 0 ? (
        <ul className={styles.failList}>
          {failing.map((probe) => (
            <li key={probe.id}>
              <strong>{probe.label}:</strong> {probe.message}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
